import { EyeIcon, EyeOffIcon } from '@iconicicons/react';
import {
  useGetPermissionsQuery,
  useUpdatePasswordMutation,
} from '../services/edgeApi';
import React, { HTMLInputTypeAttribute, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export const AccountSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [updatePassword, updatePasswordResult] = useUpdatePasswordMutation();
  const { data: sessionData = { info: undefined, rules: [] } } =
    useGetPermissionsQuery(undefined, {
      skip: !localStorage.getItem('token'),
    });
  const { info: user } = sessionData;

  const [oldPasswordType, setOldPasswordType] =
    useState<HTMLInputTypeAttribute>('password');
  const [newPasswordType, setNewPasswordType] =
    useState<HTMLInputTypeAttribute>('password');
  const [confirmPasswordType, setConfirmPasswordType] =
    useState<HTMLInputTypeAttribute>('password');

  const updatePasswordHandler = () => {
    updatePassword({
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  };

  useEffect(() => {
    if (updatePasswordResult.isSuccess) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (updatePasswordResult?.data?.first_time) {
        toast.success(
          'Password updated successfully. You will be redirected shortly.',
        );
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } else {
        toast.success('Password updated successfully.');
      }
    }
  }, [updatePasswordResult]);

  return (
    <div className="d-flex justify-content-center align-items-center w-100">
      <div
        className="m-5 p-3 gap-5 d-flex w-100 flex-column align-items-center justify-content-center bg-light rounded flex-grow-0 flex-shrink-1"
        style={{ maxWidth: '512px' }}
      >
        <h4>Account Details</h4>
        <div className="d-flex flex-column w-100">
          <h5>User Information</h5>
          <div className="row">
            <div className="col-6">
              <p className="my-0">
                <span className="fw-bold">First Name:</span>{' '}
                {user?.first_name ? (
                  user.first_name
                ) : (
                  <span className="text-muted">Not Provided</span>
                )}
              </p>
            </div>
            <div className="col">
              <p className="my-0">
                <span className="fw-bold">Last Name:</span>{' '}
                {user?.last_name ? (
                  user.last_name
                ) : (
                  <span className="text-muted">Not Provided</span>
                )}
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <p className="my-0">
                <span className="fw-bold">Email:</span> {user?.email}
              </p>
            </div>
            <div className="col">
              <p className="my-0">
                <span className="fw-bold">Role:</span> {user?.role}
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <p className="my-0">
                <span className="fw-bold">Organizations:</span>{' '}
                {user?.organizations && user.organizations.length > 0 ? (
                  user.organizations.map((org: any) => org.name).join(', ')
                ) : (
                  <span className="text-muted">No Organizations</span>
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column w-100">
          <h5>Change Password</h5>
          {user?.uses_default_password && (
            <p className="text-success">
              It looks like this is your first time logging in. Please change
              your password:
            </p>
          )}
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="input-group">
                <input
                  className="form-control rounded"
                  type={oldPasswordType}
                  placeholder="Old Password"
                  style={{ paddingRight: '50px' }}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <button
                  tabIndex={-1}
                  className="input-group-button btn btn-outline"
                  onClick={() => {
                    setOldPasswordType(
                      oldPasswordType === 'password' ? 'text' : 'password',
                    );
                  }}
                  style={{ width: '50px', marginLeft: '-50px', zIndex: 6 }}
                >
                  {oldPasswordType === 'password' ? (
                    <EyeIcon />
                  ) : (
                    <EyeOffIcon />
                  )}
                </button>
              </div>
              {(
                updatePasswordResult?.error as any
              )?.data?.issues?.old_password?.map?.((issue: string) => (
                <p key={issue} className="text-danger my-0">
                  {issue}
                </p>
              ))}
            </div>
            <div>
              <div className="input-group">
                <input
                  className="form-control rounded"
                  type={newPasswordType}
                  placeholder="New Password"
                  style={{ paddingRight: '50px' }}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  tabIndex={-1}
                  className="input-group-button btn btn-outline"
                  onClick={() => {
                    setNewPasswordType(
                      newPasswordType === 'password' ? 'text' : 'password',
                    );
                  }}
                  style={{ width: '50px', marginLeft: '-50px', zIndex: 6 }}
                >
                  {newPasswordType === 'password' ? (
                    <EyeIcon />
                  ) : (
                    <EyeOffIcon />
                  )}
                </button>
              </div>
              {(
                updatePasswordResult?.error as any
              )?.data?.issues?.new_password?.map?.((issue: string) => (
                <p key={issue} className="text-danger my-0">
                  {issue}
                </p>
              ))}
            </div>
            <div>
              <div className="input-group">
                <input
                  className="form-control rounded"
                  type={confirmPasswordType}
                  placeholder="Confirm New Password"
                  style={{ paddingRight: '50px' }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  tabIndex={-1}
                  className="input-group-button btn btn-outline"
                  onClick={() => {
                    setConfirmPasswordType(
                      confirmPasswordType === 'password' ? 'text' : 'password',
                    );
                  }}
                  style={{ width: '50px', marginLeft: '-50px', zIndex: 6 }}
                >
                  {confirmPasswordType === 'password' ? (
                    <EyeIcon />
                  ) : (
                    <EyeOffIcon />
                  )}
                </button>
              </div>

              {(
                updatePasswordResult?.error as any
              )?.data?.issues?.confirm_password?.map?.((issue: string) => (
                <p key={issue} className="text-danger my-0">
                  {issue}
                </p>
              ))}
            </div>
            <div className="d-flex justify-content-between">
              <button
                className="btn btn-primary align-self-start"
                onClick={updatePasswordHandler}
                disabled={updatePasswordResult.isLoading}
              >
                Update Password {user?.uses_default_password && 'and Continue'}
              </button>
              {updatePasswordResult.isSuccess && (
                <p className="text-success my-0 align-self-center">
                  Password updated successfully.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
